import { useEffect, useState } from "react";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Smartphone, Apple, MonitorDown, Share, Download, CheckCircle2 } from "lucide-react";

function detectDevice() {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
}

export default function InstallPage() {
  const [device, setDevice] = useState("desktop");
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setDevice(detectDevice());
    const handler = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallEvent(null);
  };

  return (
    <PublicShell>
      <section className="hero-bg pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4">
            <span className="dot" />
            <span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Get the Wakzy App</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-3">
            <span className="chrome-text">Wakzy Auto</span> <span className="gold-text">in your pocket.</span>
          </h1>
          <p className="text-slate-300 max-w-xl text-lg">
            Install <strong className="font-bold text-white">Wakzy Auto Sales &amp; Services LLC</strong> on your phone, tablet, or desktop — full-screen, fast, and works offline-friendly.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        {installed && (
          <div className="surface p-5 rounded-md mb-8 flex items-center gap-3 border-l-4" style={{ borderColor: "var(--wakzy-gold)" }} data-testid="install-success">
            <CheckCircle2 className="w-6 h-6 text-[#D4AF37]" />
            <div className="text-sm">Installed! Open from your home screen.</div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-5 mb-12">
          {/* iPhone / iPad */}
          <div className={`surface kpi-accent kpi-chrome p-6 rounded-md ${device === "ios" ? "ring-2 ring-[#D4AF37]" : ""}`} data-testid="install-ios-card">
            <Apple className="w-8 h-8 mb-3" style={{ color: "var(--accent-color)" }} />
            <div className="label-eyebrow mb-2">iPhone &amp; iPad</div>
            <h3 className="font-heading text-xl mb-3 chrome-text inline-block">iOS / Safari</h3>
            <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
              <li>Open <span className="text-[#D4AF37]">wakzyautokiosk.com</span> in Safari</li>
              <li>Tap the <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Share</strong> button at the bottom</li>
              <li>Scroll and tap <strong className="text-white">Add to Home Screen</strong></li>
              <li>Tap <strong>Add</strong> — the Wakzy app appears on your home screen</li>
            </ol>
          </div>

          {/* Android */}
          <div className={`surface kpi-accent kpi-emerald p-6 rounded-md ${device === "android" ? "ring-2 ring-[#D4AF37]" : ""}`} data-testid="install-android-card">
            <Smartphone className="w-8 h-8 mb-3" style={{ color: "var(--accent-color)" }} />
            <div className="label-eyebrow mb-2">Android Phone &amp; Tablet</div>
            <h3 className="font-heading text-xl mb-3 chrome-text inline-block">Android / Chrome</h3>
            {installEvent ? (
              <>
                <p className="text-sm text-slate-300 mb-4">One-tap install available on your device.</p>
                <Button onClick={triggerInstall} className="btn-cta rounded-sm w-full h-11" data-testid="android-install-btn">
                  <Download className="w-4 h-4 mr-2" /> Install Wakzy App
                </Button>
              </>
            ) : (
              <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
                <li>Open <span className="text-[#D4AF37]">wakzyautokiosk.com</span> in Chrome</li>
                <li>Tap the <strong>⋮</strong> menu (top right)</li>
                <li>Tap <strong className="text-white">Install app</strong> or <strong className="text-white">Add to Home screen</strong></li>
                <li>Tap <strong>Install</strong> — Wakzy launches like a native app</li>
              </ol>
            )}
          </div>

          {/* Desktop */}
          <div className={`surface kpi-accent kpi-navy p-6 rounded-md ${device === "desktop" ? "ring-2 ring-[#D4AF37]" : ""}`} data-testid="install-desktop-card">
            <MonitorDown className="w-8 h-8 mb-3" style={{ color: "var(--accent-color)" }} />
            <div className="label-eyebrow mb-2">Mac &amp; Windows</div>
            <h3 className="font-heading text-xl mb-3 chrome-text inline-block">Desktop / Chrome &amp; Edge</h3>
            {installEvent ? (
              <>
                <p className="text-sm text-slate-300 mb-4">One-click install available in this browser.</p>
                <Button onClick={triggerInstall} className="btn-cta rounded-sm w-full h-11" data-testid="desktop-install-btn">
                  <Download className="w-4 h-4 mr-2" /> Install Wakzy App
                </Button>
              </>
            ) : (
              <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside">
                <li>Open <span className="text-[#D4AF37]">wakzyautokiosk.com</span> in Chrome or Edge</li>
                <li>Look for the <Download className="inline w-4 h-4 -mt-0.5" /> install icon in the address bar</li>
                <li>Click <strong className="text-white">Install</strong></li>
                <li>Wakzy opens in its own window — pin to dock / taskbar</li>
              </ol>
            )}
          </div>
        </div>

        <div className="gradient-border" style={{ borderRadius: "16px" }}>
          <div className="gradient-border-inner p-8 lg:p-10" style={{ borderRadius: "15px" }}>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Why install</div>
                <h3 className="font-heading text-2xl chrome-text inline-block">Faster. Cleaner.</h3>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4 text-sm text-slate-300">
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Full-screen, no browser bars</div>
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Launches from home screen</div>
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Instant inventory browsing</div>
                <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" /> Push-ready for auction alerts</div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-8 text-center">
          Need help installing? Call <a href="tel:+17706472563" className="text-[#D4AF37] hover:underline">(770) 647-2563</a> or
          email <a href="mailto:info@wakzyauto.com" className="text-[#D4AF37] hover:underline">info@wakzyauto.com</a>.
        </p>
      </section>
    </PublicShell>
  );
}
